import 'bootstrap/dist/css/bootstrap.min.css';
import 'animate.css/animate.min.css';
import 'jquery/dist/jquery.min';
import 'popper.js/dist/popper.min';
import 'bootstrap/dist/js/bootstrap.min';

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue';
import Vuelidate from 'vuelidate';
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue';
import 'bootstrap-vue/dist/bootstrap-vue-icons.min.css';

import { Datetime } from 'vue-datetime';
import 'vue-datetime/dist/vue-datetime.css';

// eslint-disable-next-line no-shadow
import { focus } from './utils/directives';
import { capitalize, currency } from './utils/filters';
import router from './router';
import store from './store/store';
import App from './App';
import { resolveAssetPath } from './utils/asset-util';
import BladeSpinner from './components/BladeSpinner';

import { initializeClientVersionCheck } from './utils/version';
import { resolveAPIPath, setAPIPath, checkHealth } from './axios-config';

// Add resolveAssetPath to components and expose BladeSpinner globally for convenience
Vue.mixin({
  methods: {
    resolveAssetPath,
    formatCurrencyString(number) {
      return Math.floor(number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  },
  components: {
    BladeSpinner
  }
});

Vue.use(Vuelidate);

Vue.use(Datetime);

Vue.directive('focus', focus);

Vue.filter('capitalize', capitalize);
Vue.filter('currency', currency);

Vue.config.productionTip = false;

// Enables Bootstrap globally
Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);

/* eslint-disable no-new */
(async() => {
  /* eslint-disable no-console */

  // ---- Downtime page + auto-retry --------------------------------------------
  // Rendered straight into #app WITHOUT mounting Vue. The backend is the thing that's
  // down, so we deliberately do not run the app's init / API calls into it (that would
  // only produce noise). Recovery is a clean full reload, which re-runs this whole check.
  const RETRY_MIN_MS = 15000; // start at 15s
  const RETRY_MAX_MS = 120000; // back off up to 2 minutes
  const RETRY_JITTER_MS = 10000; // + random 0..10s each attempt (prevents synchronized herds)
  const RETRY_RAMP_STEPS = 20; // linear ramp from min to max over ~20 attempts
  const supportEmail = process.env.SUPPORT_EMAIL || 'support@redmeru.net';
  let attempt = 0;
  let retryTimer = null;

  function renderPage(code) {
    const el = document.getElementById('app');
    if (!el) return;
    el.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0f1420;color:#e8ecf3;font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;padding:24px;box-sizing:border-box;">
        <div style="max-width:480px;text-align:center;">
          <h1 style="font-size:22px;margin:0 0 10px;font-weight:600;">Service Unavailable</h1>
          <p style="color:#9aa4b2;margin:0 0 22px;font-size:15px;line-height:1.55;">
            We're having trouble reaching our backend. This is usually temporary &mdash;
            this page keeps checking on its own and should recover without a refresh.
          </p>
          <div style="font-family:ui-monospace,Menlo,Consolas,monospace;background:#1b2333;border:1px solid #2c3a55;border-radius:6px;padding:10px 14px;display:inline-block;margin-bottom:24px;font-size:13px;color:#c7d2e4;">
            status: <strong style="color:#fff;">${code}</strong>
          </div>
          <p id="blade-retry-in" style="color:#6b7686;font-size:13px;margin-top:0;min-height:18px;"></p>
          <p style="color:#9aa4b2;font-size:14px;margin-top:20px;">
            If this keeps happening, email
            <a href="mailto:${supportEmail}" style="color:#7fb0ff;text-decoration:none;">${supportEmail}</a>.
          </p>
        </div>
      </div>`;
  }

  function updateRetryLabel(ms) {
    const el = document.getElementById('blade-retry-in');
    if (el) el.textContent = `Next automatic check in ~${Math.round(ms / 1000)}s…`;
  }

  /* eslint-disable no-use-before-define */
  function scheduleNext() {
    if (retryTimer) clearTimeout(retryTimer);
    attempt += 1;
    const ramp = Math.min(1, (attempt - 1) / (RETRY_RAMP_STEPS - 1));
    const base = RETRY_MIN_MS + (RETRY_MAX_MS - RETRY_MIN_MS) * ramp;
    const delay = base + Math.random() * RETRY_JITTER_MS;
    updateRetryLabel(delay);
    retryTimer = setTimeout(runCheck, delay);
  }

  async function runCheck() {
    try {
      const { url, layer } = await resolveAPIPath();
      if (layer !== 'none' && (await checkHealth(url)) === 'ok') {
        console.log('[blade] backend healthy again; reloading.');
        window.location.reload();
        return;
      }
    } catch (e) {
      console.warn('[blade] retry check failed; will keep trying.', e);
    }
    scheduleNext();
  }
  /* eslint-enable no-use-before-define */

  function showDowntimePage(code) {
    renderPage(code);
    scheduleNext();
  }

  function boot() {
    new Vue({
      el: '#app',
      router,
      store,
      filters: { currency },
      components: { App },
      template: '<App/>',
    });
    console.log(process.env);
  }

  function statusToCode(status) {
    if (status.health === 'ok') return null;
    if (status.layer === 'none') return 'UNREACHABLE';
    if (status.layer === 'primary') return 'PRIM_DBFAIL';
    return 'FB_DBFAIL';
  }

  // ---- Two-layer status: reachability (no-cors ping) then health (CORS GET /) --
  // Reachability tells us WHICH path a user can use; health (the DB-pinging liveness
  // route) tells us whether that path is actually serving. Together they drive either
  // a normal boot or the downtime page with the right developer-facing code:
  //   UNREACHABLE  = no path open at all
  //   PRIM_DBFAIL  = direct path open but DB not responding (through it)
  //   FB_DBFAIL    = only the fallback path is open but DB not responding
  let status;
  try {
    const resolved = await resolveAPIPath();
    if (resolved.layer === 'none') {
      status = { layer: 'none', health: null, url: resolved.url };
    } else {
      status = { layer: resolved.layer, url: resolved.url, health: await checkHealth(resolved.url) };
    }
  } catch (e) {
    // The status check itself threw (shouldn't). Fail toward running the app exactly as
    // before rather than showing a possibly-wrong page.
    console.warn('[blade] status check failed; booting with default path.', e);
    initializeClientVersionCheck();
    boot();
    return;
  }

  const code = statusToCode(status);

  if (code === null) {
    setAPIPath(status.url); // pin the verified path (no-op when it's already the default)
    console.log(`[blade] API healthy via ${status.layer}; booting.`);
    initializeClientVersionCheck();
    boot();
  } else {
    console.warn(`[blade] no healthy API path (${code}); showing downtime page.`);
    showDowntimePage(code);
  }

  /* eslint-enable no-console */
})();



// WEBPACK FOOTER //
// ./src/main.js